import { useEffect, useState, useMemo } from "react"

import gsap from "gsap"
import styled from "styled-components"

import CTA from "components/CTA"
import colors from "styles/colors"
import media from "styles/media"
import { ColorType } from "types"
import useMedia from "utils/useMedia"

interface Props {
  bottomStopColors?: ColorType[]
  topStopColors?: ColorType[]
  top?: boolean
  single?: boolean
  waveHeight?: string
  waveFix?: boolean
}

export default function Waves({
  bottomStopColors = [],
  topStopColors = [],
  top = false,
  single = false,
  waveHeight = "",
  waveFix = false,
}: Props) {
  const [wave, setWave] = useState<HTMLDivElement | null>(null)
  const mobile = useMedia(false, false, false, true)
  const tablet = useMedia(false, false, true, false)
  const waveStart = useMedia("top 40%", "top 40%", "top 50%", "top bottom")
  const waveEnd = useMedia(
    "bottom 50%",
    "bottom 50%",
    "bottom 50%",
    "bottom bottom"
  )
  const bottomWaveStart = useMedia(
    "top 40%",
    "top 40%",
    "top bottom",
    "top bottom"
  )
  const waveBottomEnd = useMedia(
    "bottom 50%",
    "bottom 50%",
    "bottom bottom",
    "bottom bottom"
  )

  const topColors = useMemo(
    () =>
      topStopColors.map((item, index, arr) => {
        const offset = (100 / arr.length) * (index + 1)

        return (
          <stop
            // eslint-disable-next-line react/no-array-index-key
            key={`${item}-${index}`}
            stopColor={item}
            offset={`${offset.toString()}%`}
          />
        )
      }),
    [topStopColors]
  )

  const bottomColors = useMemo(
    () =>
      bottomStopColors.map((item, index, arr) => {
        const offset = (100 / arr.length) * (index + 1)

        return (
          <stop
            // eslint-disable-next-line react/no-array-index-key
            key={`${item}-${index}`}
            stopColor={item}
            offset={`${offset.toString()}%`}
          />
        )
      }),
    [bottomStopColors]
  )

  useEffect(() => {
    if (wave) {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wave,
          start: top ? waveStart : bottomWaveStart,
          end: top ? waveEnd : waveBottomEnd,
          scrub: true,
        },
      })

      tl.to(top ? ".Top__Wave--Top" : ".Wave__Top", {
          y: "-80px",
          ease: "back.inOut",
          duration: 3,
        }, 0).to(top ? ".Top__Wave--Bottom" : ".Wave__Bottom", {
          y: "-60px",
          ease: "back.inOut",
          duration: 2.3,
        }, 0.7)

      return () => {
        tl.kill()
      }
    }
  }, [wave, top, waveStart, waveEnd, bottomWaveStart, waveBottomEnd])

  return (
    <WaveContainer
      ref={ref => setWave(ref)}
      top={top}
      single={single}
      waveHeight={waveHeight}
      waveFix={waveFix}
    >
      <svg>
        <defs>
          <linearGradient
            id={top ? "topLinear" : "topLinear1"}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            {topColors}
          </linearGradient>
          <linearGradient
            id={top ? "bottomLinear" : "bottomLinear1"}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            {bottomColors}
          </linearGradient>
        </defs>
      </svg>

      {top && !mobile && !tablet && (
        <WaveTop waveHeight={waveHeight}>
          <svg preserveAspectRatio="none" viewBox="0 0 1660 969" fill="none">
            <path
              className="Top__Wave--Bottom"
              d="M0 878.107V969H1660V-1.89288C1660 235.59 1467.48 428.107 1230 428.107H450C201.472 428.107 0 629.579 0 878.107Z"
              fill="url(#bottomLinear)"
            />
            <path
              className="Top__Wave--Top"
              d="M0 977.799V1169H1660V97.7988C1660 335.281 1467.48 527.799 1230 527.799H450C201.472 527.799 0 729.271 0 977.799Z"
              fill="url(#topLinear)"
            />
          </svg>
        </WaveTop>
      )}

      {top && tablet && (
        <WaveTopTablet>
          <svg preserveAspectRatio="none" viewBox="0 0 1024 576" fill="none">
            <path
              className="Top__Wave--Bottom"
              d="M0 379.761V576H1024V0C1024 99.2792 943.518 179.761 844.239 179.761H200C89.5431 179.761 0 269.304 0 379.761Z"
              fill="url(#bottomLinear)"
            />
            <path
              className="Top__Wave--Top"
              d="M0 459.7V776H1024V80C1024 179.246 943.545 259.701 844.299 259.701H200C89.5431 259.701 0 349.244 0 459.7Z"
              fill="url(#topLinear)"
            />
          </svg>
        </WaveTopTablet>
      )}

      {top && mobile && (
        <WaveTopMobile>
          <svg preserveAspectRatio="none" viewBox="0 0 375 277" fill="none">
            <path
              className="Top__Wave--Bottom"
              d="M0 194.498V277H375V-5.50189C375 49.7266 330.228 94.4981 275 94.4981H100C44.7715 94.4981 0 139.27 0 194.498Z"
              fill="url(#bottomLinear)"
            />
            <path
              className="Top__Wave--Top"
              d="M0 224.652V477H375V64.6521C375 108.835 339.183 144.652 295 144.652H80C35.8172 144.652 0 180.469 0 224.652Z"
              fill="url(#topLinear)"
            />
          </svg>
        </WaveTopMobile>
      )}

      {!top && !mobile && !tablet && (
        <Wave>
          <svg preserveAspectRatio="none" viewBox="0 0 1660 1494" fill="none">
            <path
              className="Wave__Bottom"
              d="M1230 452H343C153.566 452 0 605.566 0 795V1067.5H1660V795V22C1660 259.482 1467.48 452 1230 452Z"
              fill="url(#bottomLinear1)"
            />
            <path
              className="Wave__Top"
              d="M1230 552H343C153.566 552 0 705.566 0 895V1494H1660V895V122C1660 359.482 1467.48 552 1230 552Z"
              fill={colors.blue800}
            />
          </svg>
        </Wave>
      )}
      {!top && tablet && (
        <Wave>
          <svg
            preserveAspectRatio="none"
            viewBox="0 0 1024 624"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="Wave__Bottom"
              d="M0 478.61V490.033V658H1024V490.033V98.6098C1024 198.021 943.411 278.61 844 278.61H200C89.5431 278.61 0 368.153 0 478.61Z"
              fill="url(#bottomLinear1)"
            />
            <path
              className="Wave__Top"
              d="M0 558.852V570.459V1324H1024V570.459V178.852C1024 278.263 943.411 358.852 844 358.852H200C89.5431 358.852 0 448.395 0 558.852Z"
              fill={colors.blue800}
            />
          </svg>
        </Wave>
      )}

      {!top && mobile && (
        <Wave>
          <svg
            preserveAspectRatio="none"
            viewBox="0 0 375 508"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="Wave__Bottom"
              d="M0 206.61V318.033V486H375V318.033V26.6098C375 70.7926 339.183 106.61 295 106.61H100C44.7715 106.61 0 151.381 0 206.61Z"
              fill="url(#bottomLinear1)"
            />
            <path
              className="Wave__Top"
              d="M0 268.301V393.453V1474H375V393.453V88.3008C375 132.484 339.183 168.301 295 168.301H100C44.7715 168.301 0 213.072 0 268.301Z"
              fill={colors.blue800}
            />
          </svg>
        </Wave>
      )}
    </WaveContainer>
  )
}

const WaveContainer = styled.div<{
  top: boolean | undefined
  single?: boolean
  waveHeight?: string
  waveFix?: boolean
}>`
  position: absolute;
  width: ${({ waveFix }) => (waveFix ? "101%" : "100%")};
  display: block;
  pointer-events: none;
  bottom: ${props => (props.top ? "unset" : 0)};
  top: ${props => (props.top ? 0 : "unset")};
  left: 0;
  z-index: ${({ top }) => (top ? 4 : -1)};
  ${media.fullWidth} {
    height: ${props =>
      props.waveHeight ? props.waveHeight : props.top ? "969px" : 1494};
    transform: ${props =>
      props.waveHeight
        ? `translateY(-${props.waveHeight})`
        : props.top
        ? `translateY(-969px)`
        : "unset"};
  }

  ${media.desktop} {
    height: ${props =>
      props.waveHeight ? props.waveHeight : props.top ? `56.024vw` : "90vw"};
    transform: ${props =>
      props.waveHeight
        ? `translateY(-${props.waveHeight})`
        : props.top
        ? `translateY(-56.024vw)`
        : "unset"};
  }

  ${media.tablet} {
    ${({ top }) => !top && `bottom: 75%`};
    height: ${props =>
      props.waveHeight ? props.waveHeight : props.top ? `38.25vw` : "90vw"};
    transform: ${props =>
      props.waveHeight
        ? `translateY(-${props.waveHeight})`
        : props.top
        ? `translateY(-38.25vw)`
        : "unset"};
  }

  ${media.mobile} {
    height: ${props => (props.top ? `73.867vw` : "110.467vw")};
    bottom: 16%;
    transform: ${props =>
      props.waveHeight
        ? `translateY(-${props.waveHeight})`
        : props.top
        ? `translateY(-73.867vw)`
        : "unset"};
  }
`

const WaveTop = styled.div<{ waveHeight?: string }>`
  width: 100%;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;

  svg {
    width: 100%;
    height: 100%;
  }
  ${media.fullWidth} {
    height: ${props => (props.waveHeight ? props.waveHeight : "969px")};
  }
  ${media.desktop} {
    height: ${props => (props.waveHeight ? props.waveHeight : "58.373vw")};
  }
  ${media.tablet} {
    display: none;
  }
  ${media.mobile} {
    display: none;
  }
`

const WaveTopTablet = styled.div<{ waveHeight?: string }>`
  svg {
    width: 100%;
    height: 100%;
  }
  width: 100%;
  position: absolute;
  left: 0;
  top: 0;
  height: ${props => (props.waveHeight ? props.waveHeight : "56.25vw")};
  ${media.mobile} {
    display: none;
  }
`

const WaveTopMobile = styled.div<{ waveHeight?: string }>`
  display: none;
  svg {
    width: 100%;
    height: 100%;
  }
  ${media.mobile} {
    display: inline-block;
    width: 100%;
    height: ${props => (props.waveHeight ? props.waveHeight : "73.867vw")};
    position: absolute;
    left: 0;
    top: 0;
  }
`

const Wave = styled.div`
  height: 90vw;
  width: 100%;
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 1;
  overflow: hidden;
  svg {
    width: 100%;
    height: 100%;
  }
  ${media.fullWidth} {
    height: 1494px;
  }
  ${media.tablet} {
    height: 60.938vw;
  }

  ${media.mobile} {
    height: 155.467vw;
    bottom: 266vw;
  }
`
